import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, BedDouble, CalendarClock } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { Stagger, Item } from "../components/shared";
import { useI18n } from "../i18n/LanguageContext";
import { useProjects } from "../hooks/useProjects";

const Card = ({ p }) => (
  <Item>
    <Link to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-soft transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-card">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110" />
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-navy backdrop-blur-sm">{p.tag}</span>
        {(p.emirate || "Dubai") === "Abu Dhabi" && (
          <span className="absolute right-4 top-4 rounded-full bg-gold px-3 py-1 text-xs font-semibold text-navy shadow-sm">Abu Dhabi</span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center gap-1.5 text-muted"><MapPin size={14} className="text-gold" /><span className="text-sm">{p.location}</span></div>
        <h3 className="mt-2 font-display text-2xl font-semibold leading-tight tracking-tight text-navy">{p.name}</h3>
        <p className="mt-1 text-sm text-muted">{p.developer}</p>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted">
          <span className="flex items-center gap-1.5"><BedDouble size={15} className="text-gold" /> {p.bedrooms}</span>
          <span className="flex items-center gap-1.5"><CalendarClock size={15} className="text-gold" /> {p.handover}</span>
        </div>
        <div className="mt-auto flex items-end justify-between pt-6">
          <div><span className="block text-xs uppercase tracking-wide text-muted">—</span>
            <span className="font-display text-xl font-semibold text-navy">{p.price || "Prijs op aanvraag"}{p.price && !String(p.price).includes(p.currency) && <span className="text-sm font-medium text-muted"> {p.currency}</span>}</span></div>
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-navy text-white transition-colors duration-300 group-hover:bg-gold">
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5" /></span>
        </div>
      </div>
    </Link>
  </Item>
);

const Skeleton = () => (
  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="projects-loading">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="overflow-hidden rounded-3xl bg-white shadow-soft">
        <div className="aspect-[4/3] animate-pulse bg-navy/5" />
        <div className="space-y-3 p-6">
          <div className="h-4 w-1/3 animate-pulse rounded bg-navy/5" />
          <div className="h-6 w-2/3 animate-pulse rounded bg-navy/5" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-navy/5" />
        </div>
      </div>
    ))}
  </div>
);

const emirateOf = (p) => p.emirate || "Dubai";

export default function ProjectsPage() {
  const { t, c } = useI18n();
  const p = t.projectsPage;
  const r = t.regions;
  const { projects: items, loading } = useProjects(c.PROJECTS);

  const adCount = items.filter((x) => emirateOf(x) === "Abu Dhabi").length;
  const dxbCount = items.filter((x) => emirateOf(x) === "Dubai").length;

  const [region, setRegion] = useState("all");
  const didInit = useRef(false);
  useEffect(() => {
    if (!loading && !didInit.current) {
      didInit.current = true;
      // Abu Dhabi is the primary focus — default to it when AD projects exist
      setRegion(adCount > 0 ? "Abu Dhabi" : "all");
    }
  }, [loading, adCount]);

  const filtered = region === "all" ? items : items.filter((x) => emirateOf(x) === region);

  const tabs = [
    { key: "Abu Dhabi", label: r.abudhabi, count: adCount },
    { key: "Dubai", label: r.dubai, count: dxbCount },
    { key: "all", label: r.all, count: items.length },
  ];

  return (
    <div data-testid="projects-page">
      <Seo path="/projects" title={t.seo.projects.title} description={t.seo.projects.desc}
        jsonLd={breadcrumbJsonLd([{ name: t.crumbHome, path: "/" }, { name: p.crumb, path: "/projects" }])} />
      <PageHero eyebrow={p.eyebrow} crumb={p.crumb} title={p.title} sub={p.sub} image={c.PROJECTS[3].gallery[3]} />
      <section className="py-16 sm:py-24">
        <div className="container-x">
          {!loading && items.length > 0 && (
            <div className="mb-10 flex flex-wrap gap-2.5" data-testid="projects-region-filter">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  data-testid={`region-tab-${tab.key.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setRegion(tab.key)}
                  className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                    region === tab.key
                      ? "bg-navy text-white"
                      : "border border-navy/15 text-navy hover:bg-navy/5"
                  }`}
                >
                  {tab.label} <span className="opacity-55">({tab.count})</span>
                </button>
              ))}
            </div>
          )}
          {loading ? <Skeleton /> : filtered.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-navy/20 bg-white p-12 text-center" data-testid="projects-empty">
              <p className="font-display text-xl font-semibold text-navy">—</p>
              <p className="mt-2 text-sm text-muted">No projects in this region yet.</p>
            </div>
          ) : (
            <Stagger mountAnimate key={region + (filtered[0] ? filtered[0].id : "empty")} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((pr) => <Card key={pr.id} p={pr} />)}
            </Stagger>
          )}
        </div>
      </section>
    </div>
  );
}
